import styled from "@emotion/styled";
import { Column, Row } from "@components/Flex";
import SizedBox from "@components/SizedBox";
import Text from "@components/Text";
import { useStores } from "@src/stores";
import React, { useCallback } from "react";
import BN from "@src/utils/BN";
import useWindowSize from "@src/hooks/useWindowSize";
import { IMarket, OPERATION_STATUS, PRODUCTS } from "@src/constants";
import SmallDropdown from "@components/SmallDropdown";
import shared from "@src/assets/icons/shared.svg";
import TokensPreview from "@screens/DashboardHome/MarketCard/TokensPreview";
import Skeleton from "react-loading-skeleton";
import petal from "@src/assets/icons/petal.svg";
import { useNavigate } from "react-router-dom";

interface IProps {
  market: IMarket;
}

interface MarketCardRootProps {
  borderColor: string;
}

const SharedIcon = styled.img`
  position: absolute;
  top: 10px;
  right: 10px;

  &:hover {
    cursor: pointer;
  }
`;

const StyledCard = styled.div`
  width: 25%;
  height: 130px;
  background: ${({ theme }) => theme.colors.primary50};
  padding: 20px;
  border-radius: 16px;
  position: relative;
  @media (max-width: 880px) {
    width: 100%;
    box-sizing: border-box;
  }
`;

const PetalContainer = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  border-radius: 16px;

  img {
    border-top-right-radius: 16px;
  }

  p {
    top: 5px;
    right: -12px;
    position: absolute;
  }
`;
const MarketCardRoot = styled.div<MarketCardRootProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.card.background};
  overflow: hidden;
  padding: 20px;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 4px;
    height: 100%;
    background: ${({ borderColor }) => borderColor};
    border-radius: 0;
  }
`;

const StatsCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.primary50};
  padding: 16px 16px 20px 16px;
  border-radius: 16px;
`;
const StatsContainer = styled.div`
  display: grid;
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  gap: 12px;
`;
const RomeInfoContainer = styled.div`
  display: grid;
  @media (min-width: 768px) {
    grid-template-columns: 4fr 1fr 1fr;
  }
  gap: 12px;
`;

const Icon = styled.img`
  width: 32px;
  height: 32px;
  @media (min-width: 768px) {
    width: 64px;
    height: 64px;
  }
`;
const romeInfo =
  "ROME is a stablecoin on the Waves blockchain. It is backed by yield-generating LP tokens of Puzzle Swap, which enables a unique feature: organic APY generation. This is the only page where one can mint ROME using one of the PZ-Tokens.";
const promisesUpdateSpeed = 100;
const data = [
  {
    title: "Obtain a PZ-Token*",
    descripton:
      "Acquire any PZ-Token (e.g., PZ WEBu) through an aggregator or directly from its megapool"
  },
  {
    title: "Supply to Puzzle Lend",
    descripton: "Deposit your PZ-Token into Puzzle Lend as collateral"
  },
  {
    title: "Mint ROME",
    descripton:
      "If your health level drops to zero, liquidation occurs. 20% health level is considered safe"
  },
  {
    title: "Hold & Earn Rewards",
    descripton:
      "Keep your ROME and receive payouts every few hours or use it in DeFi products"
  }
];
const Container = styled(Row)<{ color?: string }>`
  position: relative;
  padding: 4px;
  box-sizing: border-box;
  border-radius: 16px;
  height: 100%;
  align-items: center;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.primary50};
  background: ${({ theme, color }) =>
    color === "white" ? theme.colors.white : theme.colors.blue500};
  @media (max-width: 880px) {
    width: 100%;
    height: 56px;
  }
`;
const RomeMarketInfo: React.FC<IProps> = ({ market }) => {
  const { width } = useWindowSize();
  const { lendStore, accountStore } = useStores();

  const [totalSupply, setTotalSupply] = React.useState(new BN(-2));
  const fetchTotalSupply = async () => {
    if (totalSupply.eq(-2)) {
      setTotalSupply(await lendStore.getTotalSupply(market.address));
    }
    while (totalSupply.eq(-2)) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setTotalSupply(await lendStore.getTotalSupply(market.address));
    }
  };

  const [totalBorrow, setTotalBorrow] = React.useState(new BN(-2));
  const fetchTotalBorrow = async () => {
    if (totalBorrow.eq(-2)) {
      setTotalBorrow(await lendStore.getTotalBorrow(market.address));
    }
    while (totalBorrow.eq(-2)) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setTotalBorrow(await lendStore.getTotalBorrow(market.address));
    }
  };

  const [totalAccountSupply, setTotalAccountSupply] = React.useState(
    new BN(-2)
  );
  const fetchTotalAccountSupply = async () => {
    if (totalAccountSupply.eq(-2)) {
      setTotalAccountSupply(
        await lendStore.getTotalAccountSupply(market.address)
      );
    }
    while (totalAccountSupply.eq(-2)) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setTotalAccountSupply(
        await lendStore.getTotalAccountSupply(market.address)
      );
    }
  };

  const [totalAccountBorrow, setTotalAccountBorrow] = React.useState(
    new BN(-2)
  );
  const fetchTotalAccountBorrow = async () => {
    if (totalAccountBorrow.eq(-2)) {
      setTotalAccountBorrow(
        await lendStore.getTotalAccountBorrow(market.address)
      );
    }
    while (totalAccountBorrow.eq(-2)) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setTotalAccountBorrow(
        await lendStore.getTotalAccountBorrow(market.address)
      );
    }
  };

  const [maxSupplyAPY, setMaxSupplyAPY] = React.useState(new BN(-2));
  const fetchMaxSupplyAPY = async () => {
    if (maxSupplyAPY.eq(-2)) {
      setMaxSupplyAPY(await lendStore.getMaxSupplyAPY(market.address));
    }
    while (maxSupplyAPY.isZero()) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setMaxSupplyAPY(await lendStore.getMaxSupplyAPY(market.address));
    }
  };

  const [health, setHealth] = React.useState(new BN(-2));
  const fetchHealth = async () => {
    if (health.eq(-2)) {
      setHealth(await lendStore.getHealthAsync(market.address));
    }
    while (health.eq(-2)) {
      await new Promise((resolve) => setTimeout(resolve, promisesUpdateSpeed));
      setHealth(await lendStore.getHealthAsync(market.address));
    }
  };
  const navigate = useNavigate();
  const openModal = useCallback(
    (operationName: string) => {
      return navigate(
        `/3P8Df2b7ywHtLBHBe8PBVQYd3A5MdEEJAou/${operationName}/AP4Cb5xLYGH6ZigHreCZHoXpQTWDkPsG2BHqfDUx6taJ`
      );
    },
    [navigate]
  );

  Promise.all([
    fetchTotalSupply(),
    fetchTotalBorrow(),
    fetchTotalAccountSupply(),
    fetchTotalAccountBorrow(),
    fetchMaxSupplyAPY(),
    fetchHealth()
  ]);

  const mainStats = [
    {
      title: "Total Supplied ",
      value: `$ ${totalSupply.toFormat(2)} `,
      loading: totalSupply.lte(0)
    },
    {
      title: "Total Borrowed",
      value: `$ ${totalBorrow.toFormat(2)} `,
      loading: totalBorrow.lte(0)
    },
    {
      title: "Max APY",
      value: `${maxSupplyAPY.toFormat(2)} %`
    }
  ];
  return (
    <MarketCardRoot borderColor={market.color}>
      <Row>
        {width != null && width >= 760 ? (
          <>
            <Icon src={market.icon} alt="market-icon" />
            <SizedBox width={16} />
            <Column>
              <Row alignItems="center">
                <Text fitContent size="bigger" weight={500}>
                  {market.name}
                </Text>
                <SizedBox width={24} />
                <TokensPreview poolAddress={market.address} />
              </Row>
              <Text type="secondary" size="medium">
                {market.description}
              </Text>
            </Column>
          </>
        ) : (
          <Column>
            <Row>
              <Icon src={market.icon} alt="market-icon" />
              <SizedBox width={16} />
              <Text fitContent size="bigger" weight={500}>
                {market.name}
              </Text>
            </Row>
            <SizedBox height={16} />
            <TokensPreview poolAddress={market.address} />
            <SizedBox height={16} />
            <Text type="secondary" size="medium">
              {market.description}
            </Text>
          </Column>
        )}
      </Row>
      <SizedBox height={20} />
      <RomeInfoContainer>
        <StatsCard>
          <Text type="secondary" size="medium" weight={500}>
            {romeInfo}
          </Text>
        </StatsCard>
        <Container
          color={"white"}
          onClick={() => window.open(PRODUCTS.ROME_DASH, "_blank")}
        >
          <SharedIcon src={shared} />
          <Text size="medium" type="blue500" weight={500} textAlign="center">
            ROME Statistics
          </Text>
        </Container>
        <Container
          onClick={(e) => {
            lendStore.setOperationType(OPERATION_STATUS.MINT);
            openModal("mint");
          }}
        >
          <Text size="medium" type="light" weight={500} textAlign="center">
            Mint ROME
          </Text>
        </Container>
      </RomeInfoContainer>
      <SizedBox height={20} />
      <StatsContainer>
        {mainStats.map(({ title, value, loading }, index) => (
          <StatsCard>
            <Text type="secondary" size="medium" weight={500}>
              {title}
            </Text>
            {loading ? (
              <Skeleton height={24} width={64} />
            ) : (
              <Text size="medium+" weight={500}>
                {value}
              </Text>
            )}
          </StatsCard>
        ))}
      </StatsContainer>
      <SizedBox height={16} />
      <SmallDropdown
        key={`more-details-${market.address}`}
        title="Learn about ROME minting process"
      >
        {width && width < 880 ? (
          <Column style={{ gap: 10, marginBottom: 15, width: "100%" }}>
            {data.map((el, key) => (
              <StyledCard>
                <PetalContainer>
                  <img src={petal} alt="Petal" />
                  <Text style={{ color: "#ffffff" }}>{key + 1}</Text>
                </PetalContainer>
                <Text weight={500} style={{ fontSize: 17 }}>
                  {el.title}
                </Text>
                <Text style={{ marginTop: 10, fontSize: 14 }}>
                  {el.descripton}
                </Text>
              </StyledCard>
            ))}
          </Column>
        ) : (
          <Row style={{ gap: 10, marginBottom: 15, width: "100%" }}>
            {data.map((el, key) => (
              <StyledCard>
                <PetalContainer>
                  <img src={petal} alt="Petal" />
                  <Text style={{ color: "#ffffff" }}>{key + 1}</Text>
                </PetalContainer>
                <Text weight={500} style={{ fontSize: 17 }}>
                  {el.title}
                </Text>
                <Text style={{ marginTop: 10, fontSize: 14 }}>
                  {el.descripton}
                </Text>
              </StyledCard>
            ))}
          </Row>
        )}
        <Text size="medium" type="secondary">
          *PZ-Tokens are LP tokens of Puzzle Megapools consisting of multiple
          underlying assets. For example, PZ WEBu is based on WAVES, ETHu and
          BTCu.
        </Text>
        <SizedBox height={12} />
        <Text size="medium" type="secondary" onClick={()=>accountStore.setConfettiOpen(true)}>
          Click here if you are bullish for ROME
        </Text>
      </SmallDropdown>
    </MarketCardRoot>
  );
};

export default RomeMarketInfo;
